// @flow
import React from 'react';
import { getIn, useFormikContext } from 'formik';

import { TabsContainer } from '../../presentational/TabsContainer';
import {
  CLUSTER_CLUSTER_LINKING_MONTHLY_INPUTS_JSON_CONFIG,
  CLUSTER_CONNECTORS_MONTHLY_INPUTS_JSON_CONFIG,
  CLUSTER_KAFKA_MONTHLY_INPUTS_JSON_CONFIG,
  CLUSTER_KSQLDB_MONTHLY_INPUTS_JSON_CONFIG,
  TABLEFLOW_CONFIGURATION_GUIDE_LINK,
  TABLEFLOW_VERSIONS,
  CLUSTER_RTCE_MONTHLY_INPUTS_JSON_CONFIG,
  IS_RTCE_LIVE_IN_CE1,
} from '../../../constants';
import { WarningIcon } from '../../presentational/icons/WarningIcon';
import { Tooltip } from '../../presentational/Tooltip';
import { useIsFedRAMPEstimate } from '../../../custom-hooks/useIsFedRAMPEstimate';
import { filterClusterUsageTabsByFedRAMPMode } from '../../fedramp/utils';
import { useTableflowVersion } from '../../../custom-hooks/useTableflowVersion';

import { KafkaUsageContainer } from './KafkaUsageContainer';
import { KSQLDBUsageContainer } from './KSQLDBUsageContainer';
import { ClusterLinkingUsageContainer } from './ClusterLinkingUsageContainer';
import { ConnectorsTopLevelContainer } from './ConnectorsTopLevelContainer';
import { TableflowUsageContainer } from './TableflowUsageContainer';
import { TableflowV2UsageContainer } from './TableflowV2UsageContainer';
import { TABLEFLOW_WIRE_KEY_BY_VERSION } from './tableflow-config';
import { RTCEMonthlyUsageContainer } from './RTCEMonthlyUsageContainer';
import { CLUSTER_USAGE_TAB_KEYS } from './constants';

export const ClusterUsageTabsContainer = () => {
  const { errors } = useFormikContext();

  const isFedRAMPEstimate = useIsFedRAMPEstimate();
  const tableflowVersion = useTableflowVersion();
  const isTableflowV2 = tableflowVersion === TABLEFLOW_VERSIONS.V2;

  const areAnyErrorsPresentInThroughputTab =
    getIn(errors, CLUSTER_KAFKA_MONTHLY_INPUTS_JSON_CONFIG) != null;

  const areAnyErrorsPresentInConnectorsTab =
    getIn(errors, CLUSTER_CONNECTORS_MONTHLY_INPUTS_JSON_CONFIG) != null;

  const areAnyErrorsPresentInKSQLDBTab =
    getIn(errors, CLUSTER_KSQLDB_MONTHLY_INPUTS_JSON_CONFIG) != null;

  const areAnyErrorsPresentInClusterLinkingTab =
    getIn(errors, CLUSTER_CLUSTER_LINKING_MONTHLY_INPUTS_JSON_CONFIG) != null;

  const areAnyErrorsPresentInTableflowTab =
    getIn(errors, TABLEFLOW_WIRE_KEY_BY_VERSION[tableflowVersion]) != null;

  const areAnyErrorsPresentInRTCETab =
    getIn(errors, CLUSTER_RTCE_MONTHLY_INPUTS_JSON_CONFIG) != null;

  const tabPanes = [
    {
      menuItem: {
        key: CLUSTER_USAGE_TAB_KEYS.THROUGHPUT,
        icon: areAnyErrorsPresentInThroughputTab ? <WarningIcon /> : null,
        content: <>Throughput</>,
      },
      render: () => <KafkaUsageContainer />,
    },
    {
      menuItem: {
        key: CLUSTER_USAGE_TAB_KEYS.CONNECTORS,
        icon: areAnyErrorsPresentInConnectorsTab ? <WarningIcon /> : null,
        content: <>Connectors</>,
      },
      render: () => <ConnectorsTopLevelContainer />,
    },
    {
      menuItem: {
        key: CLUSTER_USAGE_TAB_KEYS.KSQLDB,
        icon: areAnyErrorsPresentInKSQLDBTab ? <WarningIcon /> : null,
        content: <>ksqlDB</>,
      },
      render: () => <KSQLDBUsageContainer />,
    },
    {
      menuItem: {
        key: CLUSTER_USAGE_TAB_KEYS.CLUSTER_LINKING,
        icon: areAnyErrorsPresentInClusterLinkingTab ? <WarningIcon /> : null,
        content: (
          <>
            Cluster Linking
            <Tooltip
              link="https://confluentinc.atlassian.net/wiki/spaces/CS/pages/3292040846/Commit+Estimator+Cluster+Linking+Configuration"
              text="Configuration Guide"
            />
          </>
        ),
      },
      render: () => <ClusterLinkingUsageContainer />,
    },
    {
      menuItem: {
        key: CLUSTER_USAGE_TAB_KEYS.TABLEFLOW,
        icon: areAnyErrorsPresentInTableflowTab ? <WarningIcon /> : null,
        content: (
          <>
            Tableflow
            <Tooltip link={TABLEFLOW_CONFIGURATION_GUIDE_LINK} text="Configuration Guide" />
          </>
        ),
      },
      render: () => (isTableflowV2 ? <TableflowV2UsageContainer /> : <TableflowUsageContainer />),
    },
    ...(IS_RTCE_LIVE_IN_CE1
      ? [
          {
            menuItem: {
              key: CLUSTER_USAGE_TAB_KEYS.RTCE,
              icon: areAnyErrorsPresentInRTCETab ? <WarningIcon /> : null,
              content: <>RTCE</>,
            },
            render: () => <RTCEMonthlyUsageContainer />,
          },
        ]
      : []),
  ];

  const filteredTabPanes = filterClusterUsageTabsByFedRAMPMode(tabPanes, isFedRAMPEstimate);

  return <TabsContainer tabPanes={filteredTabPanes} />;
};
